import React, { memo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CircularProgress from '@mui/material/CircularProgress'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

// Hooks
import useCarFormOptions from 'hooks/useCarFormOptions'
import useScheduleOptions from 'hooks/useScheduleOptions'
import { useLead } from 'hooks/useLead'

import sx from './styles'

const formatSuccessDate = (dateString) => {
  if (!dateString) return ''
  try {
    const [year, month, day] = dateString.split('/')
    const date = new Date(year, month - 1, day)
    return format(date, "EEEE, dd 'de' MMMM, yyyy", { locale: es })
  } catch (e) {
    return dateString
  }
}

const getUTMParams = () => {
  if (typeof window === 'undefined') return {}

  const searchParams = new URLSearchParams(window.location.search)
  const utms = {}
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

  utmKeys.forEach((key) => {
    if (searchParams.has(key)) {
      utms[key] = searchParams.get(key)
    }
  })

  return utms
}

const OnedayMainForm = ({
  userName, userEmail, carModel, onSuccess,
}) => {
  const {
    options: carOptions,
    values: carValues,
    handlers: carHandlers,
    status: carStatus,
  } = useCarFormOptions()

  const {
    options: scheduleOptions,
    values: scheduleValues,
    handlers: scheduleHandlers,
    status: scheduleStatus,
  } = useScheduleOptions(36)

  // Hook de Envío (Leads)
  const { mutate: submitLead, isLoading: isSubmitting } = useLead('ONEDAY')

  // Estados locales de los inputs
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [mileage, setMileage] = useState('')

  // Validación del formulario
  const isFormValid = Boolean(
    name.trim()
    && phone.trim()
    && email.trim()
    && carValues.selectedYear
    && carValues.selectedMake
    && carValues.selectedModel
    && carValues.selectedVersion
    && mileage.trim()
    && scheduleValues.selectedDate
    && scheduleValues.selectedTime,
  )

  const handleSubmit = () => {
    const selectedVersionObj = carOptions.versions.find(
      (v) => v.id === carValues.selectedVersion,
    )

    const urlUtms = getUTMParams()

    const payload = {
      year: carValues.selectedYear,
      make: carValues.selectedMake,
      model: carValues.selectedModel,
      version: selectedVersionObj ? selectedVersionObj.name : '',
      mileage,
      name,
      email,
      phone,
      scheduling_date: scheduleValues.selectedDate,
      scheduling_time: scheduleValues.selectedTime,
      ...urlUtms,
    }

    submitLead(payload, {
      onSuccess: () => {
        if (onSuccess) {
          onSuccess({
            name,
            make: carValues.selectedMake,
            model: carValues.selectedModel,
            year: carValues.selectedYear,
            date: formatSuccessDate(scheduleValues.selectedDate),
            time: scheduleValues.selectedTime,
          })
        }
      },
      onError: (error) => {
        console.error('Error al enviar el lead:', error)
        alert('Hubo un error al agendar tu cita. Intenta de nuevo.')
      },
    })
  }

  return (
    <Box sx={sx.formCard}>
      <Typography
        variant="inherit"
        sx={sx.formTitle}
      >
        {userName && carModel ? (
          <>
            {userName}
            , agenda la inspección para tu
            <span>{carModel}</span>
          </>
        ) : (
          'Agenda la inspección para tu auto'
        )}
      </Typography>

      <Grid
        container
        spacing={2}
      >
        {/* Nombre */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Nombre*
          </Typography>
          <TextField
            variant="outlined"
            placeholder={userName ? 'Jesus Lerma' : 'Escribe tu nombre'}
            sx={sx.inputBase}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>

        {/* Teléfono */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Teléfono*
          </Typography>
          <TextField
            variant="outlined"
            placeholder="55 5555 5555"
            sx={sx.inputBase}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Grid>

        {/* Mail */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Mail*
          </Typography>
          <TextField
            variant="outlined"
            placeholder={userEmail ? 'jesus.lerma@clikauto.com' : 'Escribe tu correo'}
            sx={sx.inputBase}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Grid>

        {/* Año */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Año*
          </Typography>
          <Select
            displayEmpty
            value={carValues.selectedYear}
            onChange={carHandlers.handleYearChange}
            sx={sx.selectBase}
          >
            <MenuItem
              value=""
              disabled
            >
              Selecciona una opción
            </MenuItem>
            {carOptions.years.map((year) => (
              <MenuItem
                key={year}
                value={year}
              >
                {year}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Marca */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Marca*
          </Typography>
          <Select
            displayEmpty
            value={carValues.selectedMake}
            onChange={carHandlers.handleMakeChange}
            disabled={!carValues.selectedYear || carStatus.isLoadingMakes}
            sx={sx.selectBase}
          >
            <MenuItem
              value=""
              disabled
            >
              {carStatus.isLoadingMakes ? 'Cargando...' : 'Selecciona una opción'}
            </MenuItem>
            {carOptions.makes.map((make) => (
              <MenuItem
                key={make}
                value={make}
              >
                {make}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Modelo */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Modelo*
          </Typography>
          <Select
            displayEmpty
            value={carValues.selectedModel}
            onChange={carHandlers.handleModelChange}
            disabled={!carValues.selectedMake || carStatus.isLoadingModels}
            sx={sx.selectBase}
          >
            <MenuItem
              value=""
              disabled
            >
              {carStatus.isLoadingModels ? 'Cargando...' : 'Selecciona una opción'}
            </MenuItem>
            {carOptions.models.map((model) => (
              <MenuItem
                key={model}
                value={model}
              >
                {model}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Versión */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Versión*
          </Typography>
          <Select
            displayEmpty
            value={carValues.selectedVersion}
            onChange={carHandlers.handleVersionChange}
            disabled={!carValues.selectedModel || carStatus.isLoadingVersions}
            sx={sx.selectBase}
          >
            <MenuItem
              value=""
              disabled
            >
              {carStatus.isLoadingVersions ? 'Cargando...' : 'Selecciona una opción'}
            </MenuItem>
            {carOptions.versions.map((version) => (
              <MenuItem
                key={version.id}
                value={version.id}
              >
                {version.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Kilometraje */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Kilometraje*
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Ej. 10000"
            sx={sx.inputBase}
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
          />
        </Grid>

        {/* Día de la cita */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Día de la cita*
          </Typography>
          <Select
            displayEmpty
            value={scheduleValues.selectedDate}
            onChange={scheduleHandlers.handleDateChange}
            disabled={scheduleStatus.isLoadingSchedule}
            sx={sx.selectBase}
          >
            <MenuItem
              value=""
              disabled
            >
              {scheduleStatus.isLoadingSchedule ? 'Cargando...' : 'Selecciona un día'}
            </MenuItem>

            {scheduleOptions.dates.map((dateObj) => (
              <MenuItem
                key={dateObj.value}
                value={dateObj.value}
              >
                {dateObj.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Hora de la cita */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            component="label"
            variant="inherit"
            sx={sx.label}
          >
            Hora de la cita*
          </Typography>
          <Select
            displayEmpty
            value={scheduleValues.selectedTime}
            onChange={scheduleHandlers.handleTimeChange}
            disabled={!scheduleValues.selectedDate}
            sx={sx.selectBase}
          >
            <MenuItem
              value=""
              disabled
            >
              Selecciona una hora
            </MenuItem>
            {scheduleOptions.hours.map((time) => (
              <MenuItem
                key={time}
                value={time}
              >
                {time}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Botón de Enviar */}
        <Grid size={12}>
          <Button
            variant="contained"
            endIcon={!isSubmitting && <ArrowForwardIcon />}
            sx={sx.submitBtn}
            disabled={!isFormValid || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <CircularProgress
                size={24}
                color="inherit"
              />
            ) : (
              'AGENDAR INSPECCIÓN'
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default memo(OnedayMainForm)
